import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  Timestamp,
  writeBatch,
  deleteField
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Opportunity, OpportunityActivity, OpportunityNote } from '../types';
import { postsService } from './postsService';

function deepCleanUndefined(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return obj;
  if (obj instanceof Timestamp) return obj;
  if (Array.isArray(obj)) return obj.map(deepCleanUndefined);
  if (typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = deepCleanUndefined(value);
      }
      return acc;
    }, {} as any);
  }
  return obj;
}

// Shared Firestore-doc → Opportunity mapper (timestamp conversion + defaults).
function mapOpportunityDoc(doc: any): Opportunity {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    leadIds: (data as any).leadIds || [],
    leads: (data as any).leads || [],
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    expectedCloseDate: data.expectedCloseDate?.toDate() || new Date(),
    actualCloseDate: data.actualCloseDate?.toDate(),
    nextFollowUp: data.nextFollowUp?.toDate(),
    summary: {
      totalLeads: data.summary?.totalLeads || 0,
      averageScore: data.summary?.averageScore || 0,
      highestScore: data.summary?.highestScore || 0,
      contactPoints: data.summary?.contactPoints || 0,
      keyDecisionMakers: data.summary?.keyDecisionMakers || [],
      lastActivity: data.summary?.lastActivity?.toDate() || new Date()
    },
    activities: data.activities?.map((activity: any) => ({
      ...activity,
      createdAt: activity.createdAt?.toDate() || new Date()
    })) || [],
    notes: data.notes?.map((note: any) => ({
      ...note,
      createdAt: note.createdAt?.toDate() || new Date()
    })) || [],
    company: data.company || { id: data.companyId || '', name: 'Unknown Company' }
  } as Opportunity;
}

export const opportunitiesService = {
  // Get all opportunities for organization (visible to all users in the org)
  async getOpportunities(userId: string, orgId: string): Promise<Opportunity[]> {
    try {
      // Query all opportunities in the organization (not just assigned to current user)
      // This allows team members to see and collaborate on all opportunities
      const q = query(
        collection(db, 'opportunities'),
        where('orgId', '==', orgId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(mapOpportunityDoc);
    } catch (error) {
      console.error('Error getting opportunities:', error);
      throw error;
    }
  },

  // Opportunities linked to a company — targeted single-field query (no composite
  // index needed), org-scoped client-side for defense in depth.
  async getOpportunitiesByCompany(orgId: string, companyId: string): Promise<Opportunity[]> {
    try {
      const snap = await getDocs(query(collection(db, 'opportunities'), where('companyId', '==', companyId)));
      return snap.docs.map(mapOpportunityDoc).filter(o => o.orgId === orgId);
    } catch (error) {
      console.warn('Error getting opportunities by company:', error);
      return [];
    }
  },

  // Opportunities a contact (lead) is associated with via leadIds.
  async getOpportunitiesByContact(orgId: string, leadId: string): Promise<Opportunity[]> {
    try {
      const snap = await getDocs(query(collection(db, 'opportunities'), where('leadIds', 'array-contains', leadId)));
      return snap.docs.map(mapOpportunityDoc).filter(o => o.orgId === orgId);
    } catch (error) {
      console.warn('Error getting opportunities by contact:', error);
      return [];
    }
  },

  // Listen to opportunities changes in real-time for entire organization
  onOpportunitiesChange(userId: string, orgId: string, callback: (opportunities: Opportunity[]) => void, onError?: (error: any) => void): () => void {
    // Query all opportunities in the organization for real-time updates
    const q = query(
      collection(db, 'opportunities'),
      where('orgId', '==', orgId)
    );
    
    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const opportunities = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            leadIds: (data as any).leadIds || [],
            leads: (data as any).leads || [],
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            expectedCloseDate: data.expectedCloseDate?.toDate() || new Date(),
            actualCloseDate: data.actualCloseDate?.toDate(),
            nextFollowUp: data.nextFollowUp?.toDate(),
            summary: {
              totalLeads: data.summary?.totalLeads || 0,
              averageScore: data.summary?.averageScore || 0,
              highestScore: data.summary?.highestScore || 0,
              contactPoints: data.summary?.contactPoints || 0,
              keyDecisionMakers: data.summary?.keyDecisionMakers || [],
              lastActivity: data.summary?.lastActivity?.toDate() || new Date()
            },
            activities: data.activities?.map((activity: any) => ({
              ...activity,
              createdAt: activity.createdAt?.toDate() || new Date()
            })) || [],
            notes: data.notes?.map((note: any) => ({
              ...note,
              createdAt: note.createdAt?.toDate() || new Date()
            })) || [],
            company: data.company || { id: data.companyId || '', name: 'Unknown Company' }
          };
        }) as Opportunity[];
        
        callback(opportunities);
      },
      (error) => {
        console.error('Opportunities subscription error:', error);
        if (onError) {
          onError(error);
        }
      }
    );
    
    return unsubscribe;
  },

  // Create a new opportunity
  async createOpportunity(opportunityData: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'> & { orgId?: string; createdBy?: string; createdByName?: string }): Promise<string> {
    try {
      const cleanedData = deepCleanUndefined(opportunityData);

      const docRef = await addDoc(collection(db, 'opportunities'), {
        ...cleanedData,
        orgId: (opportunityData as any).orgId,
        createdBy: (opportunityData as any).createdBy,
        productId: cleanedData.productId || null,
        product: cleanedData.product || null,
        valueSource: (opportunityData as any).valueSource || 'custom',
        leadIds: cleanedData.leadIds || [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        expectedCloseDate: opportunityData.expectedCloseDate ? Timestamp.fromDate(new Date(opportunityData.expectedCloseDate)) : Timestamp.now(),
        actualCloseDate: opportunityData.actualCloseDate ? Timestamp.fromDate(new Date(opportunityData.actualCloseDate)) : null,
        nextFollowUp: opportunityData.nextFollowUp ? Timestamp.fromDate(new Date(opportunityData.nextFollowUp)) : null,
        summary: {
          ...(cleanedData.summary || {}),
          totalLeads: (cleanedData.summary?.totalLeads || 0),
          lastActivity: Timestamp.now()
        },
        activities: cleanedData.activities || [],
        notes: cleanedData.notes || []
      });
      
      try {
        if ((opportunityData as any).orgId) {
          await postsService.createSystemPost({
            orgId: (opportunityData as any).orgId!,
            type: 'opportunity_created',
            createdById: (opportunityData as any).createdBy || 'system',
            createdByName: (opportunityData as any).createdByName || 'System',
            entityType: 'opportunity',
            entityId: docRef.id,
            entityLabel: opportunityData.title
          });

          // Check for milestones - Echobot celebration
          const allOpps = await this.getOpportunities((opportunityData as any).createdBy || '', (opportunityData as any).orgId);
          const oppCount = allOpps.length;

          // First opportunity milestone
          if (oppCount === 1) {
            await postsService.createEchobotPost({
              orgId: (opportunityData as any).orgId!,
              type: 'echobot_milestone',
              content: 'Deine erste Opportunity! Der Weg zum Erfolg beginnt! 💼',
              animation: 'celebrate',
              actionButton: {
                label: 'MEDDPICC lernen',
                action: 'explain-meddpicc'
              }
            });
          }

          // 5 opportunities milestone
          if (oppCount === 5) {
            await postsService.createEchobotPost({
              orgId: (opportunityData as any).orgId!,
              type: 'echobot_milestone',
              content: '5 Opportunities in der Pipeline! Weiter so! 🎯',
              animation: 'celebrate'
            });
          }
        }
      } catch (e) {
        console.error('Error creating feed posts:', e);
      }
      console.log('Opportunity created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating opportunity:', error);
      throw error;
    }
  },

  // Update an opportunity
  async updateOpportunity(opportunityId: string, updates: Partial<Opportunity>): Promise<void> {
    try {
      const cleanedUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        // undefined = "no change", null = "clear this field" (removed via deleteField)
        if (value === undefined) return acc;
        acc[key] = value === null ? deleteField() : value;
        return acc;
      }, {} as any);

      const opportunityRef = doc(db, 'opportunities', opportunityId);
      
      if (cleanedUpdates.expectedCloseDate) {
        cleanedUpdates.expectedCloseDate = Timestamp.fromDate(new Date(cleanedUpdates.expectedCloseDate));
      }
      if (cleanedUpdates.actualCloseDate) {
        cleanedUpdates.actualCloseDate = Timestamp.fromDate(new Date(cleanedUpdates.actualCloseDate));
      }
      if (cleanedUpdates.nextFollowUp) {
        cleanedUpdates.nextFollowUp = Timestamp.fromDate(new Date(cleanedUpdates.nextFollowUp));
      }
      
      if (cleanedUpdates.activities) {
        cleanedUpdates.activities = cleanedUpdates.activities.map((activity: OpportunityActivity) => ({
          ...activity,
          createdAt: Timestamp.fromDate(new Date(activity.createdAt))
        }));
      }
      
      if (cleanedUpdates.notes) {
        cleanedUpdates.notes = cleanedUpdates.notes.map((note: OpportunityNote) => ({
          ...note,
          createdAt: Timestamp.fromDate(new Date(note.createdAt))
        }));
      }
      
      await updateDoc(opportunityRef, {
        ...cleanedUpdates,
        updatedAt: Timestamp.now()
      });
      
      console.log('Opportunity updated:', opportunityId);

      // If stage changed in updates, create system post
      if (cleanedUpdates.stage) {
        try {
          const snap = await getDoc(opportunityRef);
          const opp = snap.data() as any;
          if (opp?.orgId) {
            await postsService.createSystemPost({
              orgId: opp.orgId,
              type: 'opportunity_stage_changed',
              createdById: (updates as any).updatedBy || opp.createdBy || 'system',
              createdByName: 'System',
              entityType: 'opportunity',
              entityId: opportunityId,
              entityLabel: opp.title || 'Opportunity'
            });
          }

          // Unify customer lifecycle: when an opportunity is won, promote its
          // company to a customer (idempotent, non-blocking — never fails the
          // opportunity update). Done via Firestore directly to avoid a service
          // import cycle.
          if (cleanedUpdates.stage === 'won' && opp?.companyId) {
            try {
              const companyRef = doc(db, 'companies', opp.companyId);
              const cSnap = await getDoc(companyRef);
              if (cSnap.exists() && !(cSnap.data() as any)?.isCustomer) {
                await updateDoc(companyRef, {
                  isCustomer: true,
                  customerSince: (cSnap.data() as any)?.customerSince || Timestamp.now(),
                  updatedAt: Timestamp.now()
                });
                console.log('Company promoted to customer on won opportunity:', opp.companyId);
              }
            } catch (e) {
              console.error('Auto-promote company to customer failed:', e);
            }
          }
        } catch {}
      }
    } catch (error) {
      console.error('Error updating opportunity:', error);
      throw error;
    }
  },

  // Delete an opportunity
  async deleteOpportunity(opportunityId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'opportunities', opportunityId));
      console.log('Opportunity deleted:', opportunityId);
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      throw error;
    }
  },

  // Add a note to an opportunity
  async addNoteToOpportunity(
    opportunityId: string,
    content: string,
    type: 'general' | 'strategy' | 'concern' | 'competitive',
    createdBy: string
  ): Promise<void> {
    try {
      const opportunityRef = doc(db, 'opportunities', opportunityId);
      const snap = await getDoc(opportunityRef);
      
      if (!snap.exists()) {
        throw new Error('Opportunity not found');
      }

      const currentOpportunity = snap.data() as Opportunity;
      const existingNotes = currentOpportunity.notes || [];

      const newNote: OpportunityNote = {
        id: `note_${Date.now()}`,
        content,
        type,
        createdAt: new Date(),
        createdBy
      };

      await updateDoc(opportunityRef, {
        notes: [...existingNotes, newNote],
        updatedAt: Timestamp.now()
      });

      // Create activity for note
      const existingActivities = currentOpportunity.activities || [];
      const newActivity: OpportunityActivity = {
        id: `activity_${Date.now()}`,
        type: 'note_added',
        description: `Added a ${type} note`,
        createdAt: new Date(),
        createdBy,
        metadata: { noteType: type }
      };

      await updateDoc(opportunityRef, {
        activities: [...existingActivities, newActivity]
      });

      console.log('Note added to opportunity:', opportunityId);
    } catch (error) {
      console.error('Error adding note to opportunity:', error);
      throw error;
    }
  },

  // Add a lead to an opportunity (and update lead.opportunityId)
  async addLeadToOpportunity(opportunityId: string, leadId: string): Promise<void> {
    try {
      const opportunityRef = doc(db, 'opportunities', opportunityId);
      const leadRef = doc(db, 'leads', leadId);

      const opportunitySnap = await getDoc(opportunityRef);
      if (!opportunitySnap.exists()) throw new Error('Opportunity not found');
      const opportunity = opportunitySnap.data() as any;

      const currentLeadIds: string[] = opportunity.leadIds || [];
      if (currentLeadIds.includes(leadId)) return; // already linked

      const updatedLeadIds = [...currentLeadIds, leadId];

      // Update opportunity leadIds and summary
      const newSummary = {
        ...(opportunity.summary || {}),
        totalLeads: (opportunity.summary?.totalLeads || 0) + 1,
        lastActivity: Timestamp.now(),
      };

      await updateDoc(opportunityRef, {
        leadIds: updatedLeadIds,
        updatedAt: Timestamp.now(),
        summary: newSummary,
        activities: [
          ...(opportunity.activities || []),
          {
            id: Date.now().toString(),
            type: 'lead_created',
            description: `Lead ${leadId} linked to opportunity`,
            createdAt: Timestamp.now(),
            createdBy: 'system'
          }
        ]
      });

      // Update the lead's reference to this opportunity
      await updateDoc(leadRef, {
        opportunityId,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error adding lead to opportunity:', error);
      throw error;
    }
  }
};
